'use client';

import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  DOCUMENT_BY_NUMBER_QUERY,
  type DocumentType,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';

export interface ResolvedBeleg {
  id: string;
  documentNumber: string;
  type: DocumentType;
}

export interface UseResolveBelegByNumberResult {
  resolve: (documentNumber: string) => Promise<ResolvedBeleg | null>;
  isLoading: boolean;
}

export function useResolveBelegByNumber(): UseResolveBelegByNumberResult {
  const client = useApolloClient();
  const { adminQueryVars } = useAdmin();
  const [isLoading, setIsLoading] = useState(false);

  const resolve = useCallback(
    async (documentNumber: string): Promise<ResolvedBeleg | null> => {
      if (!documentNumber) return null;
      setIsLoading(true);
      try {
        const { data } = await client.query<{
          documentByNumber: ResolvedBeleg | null;
        }>({
          query: DOCUMENT_BY_NUMBER_QUERY,
          variables: { number: documentNumber, ...adminQueryVars },
          fetchPolicy: 'network-only',
        });
        if (!data?.documentByNumber) {
          toast.warning(`Beleg ${documentNumber} nicht gefunden`);
          return null;
        }
        return data.documentByNumber;
      } catch (err) {
        console.error('useResolveBelegByNumber:', err);
        toast.error(`Beleg ${documentNumber} konnte nicht geladen werden`);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [client, adminQueryVars]
  );

  return { resolve, isLoading };
}
